import { create } from 'zustand';

import { apiGet, apiPost } from '@/core/api/api-client';
import { useRuntimeConfigStore } from '@/core/config/runtime-config.loader';

import { type Action, PERMISSIONS, type Resource } from './permissions';

export type Role = 'admin' | 'analyst';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  pictureUrl?: string;
  role: Role;
}

export interface AuthConfig {
  auth_mode: string;
  providers: string[];
}

interface AuthState {
  user: CurrentUser | null;
  initialized: boolean;
  init: () => Promise<void>;
  refreshMe: () => Promise<CurrentUser | null>;
  fetchConfig: () => Promise<AuthConfig>;
  logout: () => Promise<void>;
  clearUser: () => void;
  isAuthenticated: () => boolean;
  hasRole: (...roles: Role[]) => boolean;
  can: (resource: Resource, action: Action) => boolean;
}

function authBaseUrl(): string {
  const url = useRuntimeConfigStore.getState().config?.authBaseUrl;
  // Explicit undefined check — empty string is a valid "same-origin" value
  // for production deployments where the SPA and auth endpoints share an
  // origin via nginx proxy (runtime-config.prod.json sets authBaseUrl: "").
  if (url === undefined) {
    throw new Error('authBaseUrl not configured; runtime config must load before auth actions');
  }
  return url;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  initialized: false,

  init: async () => {
    await get().refreshMe();
  },

  refreshMe: async () => {
    try {
      const me = await apiGet<CurrentUser>(`${authBaseUrl()}/auth/me`);
      set({ user: me });
      return me;
    } catch {
      set({ user: null });
      return null;
    } finally {
      set({ initialized: true });
    }
  },

  fetchConfig: async () => apiGet<AuthConfig>(`${authBaseUrl()}/auth/config`),

  logout: async () => {
    try {
      await apiPost<void>(`${authBaseUrl()}/logout`, {});
    } finally {
      set({ user: null, initialized: true });
    }
  },

  clearUser: () => set({ user: null }),

  isAuthenticated: () => get().user !== null,

  hasRole: (...roles) => {
    const role = get().user?.role;
    return !!role && roles.includes(role);
  },

  can: (resource, action) => {
    if (!get().isAuthenticated()) return false;
    const rule = PERMISSIONS[resource][action];
    if (rule === 'auth') return true;
    return get().hasRole(...rule);
  },
}));

// Vanilla access for non-React consumers (e.g., api/auth-error-handler.ts).
export const authStore = useAuthStore;

export const selectUser = (s: AuthState): CurrentUser | null => s.user;
export const selectInitialized = (s: AuthState): boolean => s.initialized;
export const selectIsAuthenticated = (s: AuthState): boolean => s.user !== null;
